import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { finTier } from "@/lib/finRole";
import { Landmark, Coins, BookOpen, HandCoins, CheckSquare, Wallet, Receipt, Scale, TrendingUp, FileBarChart, Library, ListTree, Waves, ClipboardList, ShieldCheck, Lock, ChevronRight } from "lucide-react";

const ALL = ["MGR", "OPS", "CTRL"];
const SECTIONS = {
  "kas-bank": { title: "Kas & Bank", sub: "Uang masuk, keluar & persetujuan pembayaran", items: [
    { to: "/cash-bank", label: "Cash & Bank", icon: Landmark, who: ALL },
    { to: "/petty-cash", label: "Petty Cash", icon: Coins, who: ALL },
    { to: "/fin/kas-keluar", label: "Kas Keluar & Petty Cash", opsLabel: "Pengajuan Kas Keluar", icon: Wallet, who: ALL, badge: { MGR: "cash_pending", OPS: "cash_to_verify" } },
    { to: "/daybook", label: "Buku Kas Harian", icon: BookOpen, who: ["MGR", "CTRL"] },
    { to: "/or-payments?tab=queue", label: "Approval OR", opsLabel: "Verifikasi OR", icon: HandCoins, who: ALL, badge: { MGR: "or_pending", OPS: "or_to_verify" } },
    { to: "/fin/approval-pembayaran", label: "Approval Pembayaran", opsLabel: "Status Pengajuan Pembayaran", icon: CheckSquare, who: ALL, badge: { MGR: "ap_pending", OPS: "ap_to_verify" } },
  ]},
  "piutang-utang": { title: "Piutang & Utang", sub: "Invoice, tagihan supplier & rekonsiliasi asuransi", items: [
    { to: "/finance", label: "Accounts Receivable", icon: Wallet, who: ALL },
    { to: "/accounts-payable", label: "Accounts Payable", icon: Receipt, who: ALL },
    { to: "/recon-dashboard", label: "Rekonsiliasi Asuransi", icon: Scale, who: [...ALL, "BM"], badge: { MGR: "recon_pending" } },
  ]},
  "laporan-kontrol": { title: "Laporan & Kontrol", sub: "Rahasia · hanya Finance Manager, Controller & Owner", items: [
    { to: "/accounting?tab=pnl", label: "Laba Rugi", icon: TrendingUp, who: ["MGR", "CTRL"] },
    { to: "/accounting?tab=bs", label: "Neraca", icon: Scale, who: ["MGR", "CTRL"] },
    { to: "/accounting?tab=gl", label: "General Ledger", icon: Library, who: ["MGR", "CTRL"] },
    { to: "/accounting?tab=journal", label: "Journal", icon: BookOpen, who: ["MGR", "CTRL"] },
    { to: "/accounting?tab=tb", label: "Trial Balance", icon: Scale, who: ["MGR", "CTRL"] },
    { to: "/fin/coa", label: "Chart of Accounts", icon: ListTree, who: ["MGR", "CTRL"] },
    { to: "/fin/cash-flow", label: "Cash Flow", icon: Waves, who: ["MGR", "CTRL"] },
    { to: "/report-control", label: "Report & Control", icon: FileBarChart, who: ["MGR", "CTRL"] },
    { to: "/fin/audit-log", label: "Audit Log", icon: ShieldCheck, who: ["MGR", "CTRL"] },
    { to: "/fin/closing", label: "Closing Bulanan", icon: Lock, who: ["MGR", "CTRL"] },
  ]},
};

function Tile({ it, tier, count, onClick }) {
  const Icon = it.icon;
  return (
    <button data-testid={`fin-hub-tile-${it.to.replace(/[^a-z]/gi, "-")}`} onClick={onClick} className="relative flex min-h-[104px] flex-col justify-between rounded-2xl border border-[#E2E5EA] bg-white p-4 text-left shadow-[0_1px_2px_rgba(17,24,39,0.06)] transition-transform hover:-translate-y-0.5 active:scale-[0.98]">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8EEF5] text-[#1E3A5F]"><Icon size={24} strokeWidth={2.2} /></span>
      <span className="mt-2 flex items-center justify-between gap-1 text-[15px] font-extrabold leading-tight text-[#111827]">{tier === "OPS" && it.opsLabel ? it.opsLabel : it.label}<ChevronRight size={16} className="shrink-0 text-[#94A3B8]" /></span>
      {count > 0 && <span data-testid="fin-hub-badge" className="absolute right-3 top-3 min-w-[24px] rounded-full bg-[#B91C1C] px-1.5 py-0.5 text-center text-[12px] font-bold text-white">{count}</span>}
    </button>
  );
}

export default function FinanceHub({ section }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const tier = finTier(user);
  const [b, setB] = useState({});
  useEffect(() => { api.get("/finance/badges").then((r) => setB(r.data || {})).catch(() => {}); }, []);
  const s = SECTIONS[section];
  const items = s.items.filter((i) => i.who.includes(tier));
  return (
    <div data-testid={`fin-hub-${section}`} className="mx-auto max-w-3xl">
      <h1 className="font-heading text-[24px] font-extrabold text-[#111827]">{s.title}</h1>
      <p className="mt-0.5 text-[14px] font-medium text-[#475569]">{s.sub}</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {items.map((it) => <Tile key={it.to} it={it} tier={tier} count={it.badge?.[tier] ? b[it.badge[tier]] : 0} onClick={() => nav(it.to)} />)}
      </div>
      {items.length === 0 && <p className="mt-8 text-center text-[14px] text-[#475569]">Tidak ada menu untuk role Anda.</p>}
      <div className="mt-6 flex items-center gap-2 rounded-xl border border-[#E2E5EA] bg-[#FAFBFC] p-3 text-[13px] text-[#334155]"><ClipboardList size={16} className="text-[#1E3A5F]" />Alur: Admin INPUT → Manager REVIEW → APPROVE / REJECT → diposting setelah dana terbukti diterima/dibayar.</div>
    </div>
  );
}
